import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import PhoneInput from "../common/phoneInput/phoneInput";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import PasswordField from "../common/passwordField/passwordField";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import RegisterStaffSkeleton from "./registerStaffSkeleton/registerStaffSkeleton";
import logo from "../../images/LogoFullText.png";
import "./registerStaff.css";

const RegisterStaff = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const navigate = useNavigate();

	const [rememberMe, setRememberMe] = useState(false);
	const [verifying, setVerifying] = useState(true);
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [shopId, setShopId] = useState("");
	const [inviteId, setInviteId] = useState("");
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [activeModal, setActiveModal] = useState(null);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
    const [blockingError, setBlockingError] = useState(false);

	const { userData, setUserData, language } = useContext(CombinedContext);

	const showBanner = (type, text, duration = 2500) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	const showPersistentBanner = (type, text) => {
		setBanner({ type, text });
		setExiting(false);
        if (type === "error") setBlockingError(true);
	};

	useEffect(() => {
		const verifyInvite = async () => {
			try {
				const res = await companyBasedRequest.verifyInvite(token);
				setInviteId(res.inviteId);
				setShopId(res.shopId);
				setFirstName(res.firstName);
				setLastName(res.lastName);
				setEmail(res.email || "");

				if (res.adminVerified && !res.used) {
					if (!userData || !userData.cognitoTokens?.IdToken) {
						navigate("/signin");
						return;
					}
				}

				sessionStorage.setItem("inviteVerified", "true");
			} catch (err) {
				const code = err.response?.data?.error;

				const messages = {
					staff_exists: {
						es: "Ya existe una cuenta de personal activa con este correo electrónico.",
						en: "A staff account is already active with this email address.",
					},
					customer_exists: {
						es: "Ya existe una cuenta de cliente activa con este correo electrónico. Para continuar, desactiva tu cuenta de cliente o solicita una invitación con otro correo.",
						en: "A customer account is already active with this email address. To proceed, disable your customer account or request a staff invite using a different email address.",
					},
				};

				const msg =
					messages[code]?.[language] ??
					(language === "es" ? "Este enlace ya fue usado o expiró" : "This invite link has already been used or has expired");

				showPersistentBanner("error", msg);
			} finally {
				setVerifying(false);
			}
		};

		verifyInvite();
	}, [token]);

	if (verifying) return <RegisterStaffSkeleton />;

	const normalizePhoneNumber = (raw) => {
		if (!raw) return "";
		let cleaned = raw.trim().replace(/[^0-9+]/g, "");
		if (cleaned.startsWith("++")) cleaned = cleaned.replace(/^\++/, "+");
		if (!cleaned.startsWith("+")) cleaned = "+1" + cleaned;
		cleaned = cleaned.replace(/(?!^)\+/g, "");
		if (!/^\+\d{10,15}$/.test(cleaned)) return "";
		return cleaned;
	};

	function isStrongPassword(pwd) {
		return (
			typeof pwd === "string" &&
			pwd.length >= 8 &&
			/[A-Z]/.test(pwd) &&
			/[a-z]/.test(pwd) &&
			/[0-9]/.test(pwd) &&
			/[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(pwd)
		);
	}

	const handleRegisterAdmin = async () => {
		if (!firstName || !lastName || !phoneNumber || !password || !confirmPassword) {
			showBanner("error", language === "es" ? "Por favor completa todos los campos obligatorios" : "Please fill out all required fields");
			return;
		}

		if (!acceptedTerms) {
			showBanner("error", translations[language].registerStaff.errors.mustAcceptTerms);
			return;
		}

		if (!isStrongPassword(password)) {
			showBanner(
				"error",
				language === "es"
					? "La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial"
					: "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character",
			);
			return;
		}

		if (password !== confirmPassword) {
			showBanner("error", language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
			return;
		}

		setLoading(true);

		try {
			const payload = {
				token,
				shopId,
				inviteId,
				email,
				password,
				firstName,
				lastName,
				phone: normalizePhoneNumber(phoneNumber),
				acceptedTermsVersion: "1.0",
			};

			const res = await companyBasedRequest.registerStaff(payload);

			const formattedData = {
				...res,
				cognitoTokens: {
					IdToken: res.cognitoTokens?.IdToken || res.IdToken,
					AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
					RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
				},
			};

			setUserData(formattedData);

			const storage = rememberMe ? localStorage : sessionStorage;
			if (formattedData.cognitoTokens?.IdToken) storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
			if (formattedData.cognitoTokens?.AccessToken) storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
			if (formattedData.cognitoTokens?.RefreshToken) storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);
			storage.setItem("user", JSON.stringify(formattedData.user));

			navigate("/calendar");
		} catch (err) {
			console.error("Register staff failed:", err);
			showPersistentBanner("error", err.message || (language === "es" ? "Error al registrar usuario" : "Failed to register staff"));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="sf-rst-root">
			<div className="sf-rst-card">
				<h1 className="sf-rst-title">{translations[language].registerStaff.step1.title}</h1>

				{/* Email — prefilled, read only */}
				<div className="sf-rst-field">
					<label className="sf-rst-label">{translations[language].registerStaff.step1.email}</label>
					<input
						className="sf-rst-input sf-rst-input-disabled"
						type="email"
						value={email}
						placeholder={translations[language].registerStaff.step1.email}
						disabled
					/>
				</div>

				{/* Name row */}
				<div className="sf-rst-row2">
					<div className="sf-rst-field">
						<label className="sf-rst-label">{translations[language].registerStaff.step1.firstName}</label>
						<input
							className="sf-rst-input"
							placeholder={translations[language].registerStaff.step1.firstName}
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
						/>
					</div>
					<div className="sf-rst-field">
						<label className="sf-rst-label">{translations[language].registerStaff.step1.lastName}</label>
						<input
							className="sf-rst-input"
							placeholder={translations[language].registerStaff.step1.lastName}
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
						/>
					</div>
				</div>

				{/* Phone */}
				<div className="sf-rst-field">
					<label className="sf-rst-label">{language === "es" ? "Teléfono" : "Phone"}</label>
					<PhoneInput businessPhone={phoneNumber} setBusinessPhone={setPhoneNumber} />
				</div>

				{/* Password */}
				<div className="sf-rst-field">
					<label className="sf-rst-label">{translations[language].registerStaff.step1.password}</label>
					<PasswordField
						value={password}
						onChange={setPassword}
						placeholder={translations[language].registerStaff.step1.password}
						autoComplete="new-password"
					/>
				</div>

				{/* Confirm password */}
				<div className="sf-rst-field">
					<label className="sf-rst-label">{translations[language].registerStaff.step1.confirmPassword}</label>
					<PasswordField
						value={confirmPassword}
						onChange={setConfirmPassword}
						placeholder={translations[language].registerStaff.step1.confirmPassword}
						autoComplete="new-password"
					/>
				</div>

				{/* Remember me */}
				<div className="sf-rst-check-row">
					<input type="checkbox" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="sf-rst-checkbox" />
					<label htmlFor="rememberMe" className="sf-rst-check-label">
						{translations[language].registerStaff.step1.remember}
					</label>
				</div>

				{/* Terms */}
				<div className="sf-rst-check-row">
					<input
						type="checkbox"
						id="acceptTerms"
						checked={acceptedTerms}
						onChange={(e) => setAcceptedTerms(e.target.checked)}
						className="sf-rst-checkbox"
					/>
					<label htmlFor="acceptTerms" className="sf-rst-check-label">
						{translations[language].register.acceptTerms}{" "}
						<span className="sf-rst-terms-link" onClick={() => setActiveModal("terms")}>
							{translations[language].register.termsLink}
						</span>
					</label>
				</div>

				{/* Submit */}
				<button className="sf-rst-submit" onClick={handleRegisterAdmin} disabled={loading || !password || !confirmPassword || blockingError}>
					{loading ? translations[language].registerStaff.step1.registering : translations[language].registerStaff.step1.register}
				</button>
			</div>

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
			{activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
		</div>
	);
};

export default RegisterStaff;
